import {
  mcpConnectorCatalog,
  type McpConnectorPreset,
} from '@kanbu/schema/contracts/tools';
import { Trans } from '@lingui/react/macro';
import { Badge } from '@utima/ui';
import { Compass, ExternalLink } from 'lucide-react';

import { McpProviderIcon } from './McpProviderIcon';

export interface McpConnectorCatalogProps {
  onSelect: (preset: McpConnectorPreset) => void;
}

const MCP_DIRECTORY_URL = 'https://mcpservers.org/';

/**
 * Curated one-click connector presets (Phase 3C). Selecting a card pre-fills
 * the integration dialog with the provider's server URL + OAuth profile.
 */
export function McpConnectorCatalog({ onSelect }: McpConnectorCatalogProps) {
  return (
    <div className='space-y-2'>
      <p className='text-sm text-muted-fg'>
        <Trans>Quick connect a popular provider, or add a custom server.</Trans>
      </p>
      <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        {mcpConnectorCatalog.map(preset => {
          const isDisabled =
            preset.status === 'planned' || preset.status === 'on_request';

          return (
            <button
              key={preset.id}
              type='button'
              disabled={isDisabled}
              onClick={() => onSelect(preset)}
              className='bg-card flex flex-col rounded-radius border border-border p-3 text-left transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border'
            >
              <div className='flex items-center justify-between gap-2'>
                <span className='flex items-center gap-2 font-medium'>
                  <McpProviderIcon
                    connectorId={preset.id}
                    name={preset.name}
                    className='size-4 shrink-0'
                  />
                  {preset.name}
                </span>
                {preset.status !== 'available' && (
                  <Badge
                    variant={preset.status === 'beta' ? 'warning' : 'muted'}
                    size='xs'
                  >
                    {preset.status === 'beta' && <Trans>Beta</Trans>}
                    {preset.status === 'planned' && <Trans>Soon</Trans>}
                    {preset.status === 'on_request' && (
                      <Trans>On request</Trans>
                    )}
                  </Badge>
                )}
              </div>
              <span className='mt-1 flex-1 text-xs text-muted-fg'>
                {preset.description}
              </span>
              {preset.docsUrl && (
                <a
                  href={preset.docsUrl}
                  target='_blank'
                  rel='noreferrer'
                  onClick={e => e.stopPropagation()}
                  className='mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline'
                >
                  <Trans>Docs</Trans>
                  <ExternalLink className='size-3' />
                </a>
              )}
            </button>
          );
        })}
        <a
          href={MCP_DIRECTORY_URL}
          target='_blank'
          rel='noreferrer'
          className='bg-card flex flex-col rounded-radius border border-dashed border-border p-3 text-left transition-colors hover:border-primary'
        >
          <div className='flex items-center gap-2 font-medium'>
            <Compass className='size-4 shrink-0 text-muted-fg' />
            <Trans>Something else?</Trans>
          </div>
          <span className='mt-1 flex-1 text-xs text-muted-fg'>
            <Trans>
              Hundreds more official and community MCP servers are out there —
              browse mcpservers.org and add any server URL as a custom
              connection.
            </Trans>
          </span>
          <span className='mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline'>
            <Trans>Browse directory</Trans>
            <ExternalLink className='size-3' />
          </span>
        </a>
      </div>
    </div>
  );
}
